import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
  Chip,
  Tabs,
  Tab,
  Paper,
  Divider,
} from '@mui/material';
import { 
  VideoCall as VideoIcon,
  Timer as TimerIcon,
  HighQuality as QualityIcon,
  Info as InfoIcon,
  Image as ImageIcon,
  TextFields as TextIcon
} from '@mui/icons-material';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`video-gen-tabpanel-${index}`}
      aria-labelledby={`video-gen-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function VideoGenerationDialog({ open, onClose, onVideoGenerated, accountId, microSiteId }) {
  const [tabValue, setTabValue] = useState(0);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(5);
  const [quality, setQuality] = useState('standard');
  const [startImage, setStartImage] = useState('');
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setProgress(0);
    setStatusMessage('Initializing video generation...');

    try {
      // Generate and store the video in one API call
      const response = await fetch('/api/ai/generate-video', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          prompt,
          duration,
          quality,
          accountId,
          microSiteId,
          startImage: tabValue === 1 ? startImage : null // Only include startImage if on the image-to-video tab
        }),
      });

      // Check if response is OK
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || errorData.details || 'Failed to generate video');
        } else {
          // Handle non-JSON responses
          const text = await response.text();
          console.error('Non-JSON response:', text);
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }

      // Start progress simulation
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          
          // Update status message based on progress
          if (prev < 20) {
            setStatusMessage('Generating video frames...');
          } else if (prev < 50) {
            setStatusMessage('Processing video sequence...');
          } else if (prev < 80) {
            setStatusMessage('Finalizing video...');
          } else {
            setStatusMessage('Almost done...');
          }
          
          return prev + 1;
        });
      }, duration * 200); // Roughly simulate the generation time
      
      // Safely parse JSON
      let data;
      try {
        data = await response.json();
        console.log('Video generation response:', data);
        
        // Clear the interval and set progress to 100%
        clearInterval(progressInterval);
        setProgress(100);
        setStatusMessage('Video generation complete!');
        
      } catch (jsonError) {
        console.error('JSON parsing error:', jsonError);
        throw new Error('Invalid response format from server');
      }
      
      // Set the generated video from the response
      setGeneratedVideo(data.video.url);
      
      // Automatically use the video since it's already stored
      if (typeof onVideoGenerated === 'function') {
        console.log('Calling onVideoGenerated with:', data.video);
        onVideoGenerated(data.video);
      } else {
        console.warn('onVideoGenerated is not a function');
      }
      
      onClose();
    } catch (error) {
      console.error('Video generation error:', error);
      setError(error.message || 'Failed to generate video');
      setLoading(false);
    }
  };

  const handleDurationChange = (event, newDuration) => {
    if (newDuration !== null) {
      setDuration(newDuration);
    }
  };

  const isValidImageUrl = (url) => {
    if (!url) return false;
    
    // Check for common image extensions
    if (url.match(/\.(jpeg|jpg|gif|png|webp|bmp|svg)$/i)) {
      return true;
    }
    
    // Check for known image hosting services that might not have extensions
    if (
      url.startsWith('https://replicate.delivery/') ||
      url.startsWith('https://tything.net/api/images/') ||
      url.includes('cloudinary.com') ||
      url.includes('imgur.com') ||
      url.includes('unsplash.com') ||
      url.includes('pexels.com') ||
      url.includes('pixabay.com')
    ) {
      return true;
    }
    
    // For other URLs, we'll assume they're valid if they contain 'image' in the path
    // This is a bit permissive but helps with various API endpoints
    if (url.includes('/image') || url.includes('/images/')) {
      return true;
    }
    
    return false;
  };

  const getModelInfo = () => {
    if (tabValue === 0) {
      return {
        name: "Zeroscope XL",
        description: "A text-to-video model that generates videos from text descriptions."
      };
    } else {
      return {
        name: "Kling v1.6",
        description: "An image-to-video model that animates still images based on text prompts."
      };
    }
  };

  const modelInfo = getModelInfo();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <VideoIcon sx={{ mr: 1 }} />
          Generate Video with AI
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
            <Typography variant="subtitle2" fontWeight="bold">
              AI Video Generation with {modelInfo.name}
            </Typography>
            <Typography variant="body2">
              {modelInfo.description} Generation takes approximately {duration === 5 ? '2-3' : '4-6'} minutes for a {duration}-second video.
            </Typography>
          </Alert>

          <Paper sx={{ mb: 3 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="video generation method"
              variant="fullWidth"
            >
              <Tab 
                icon={<TextIcon />} 
                label="Text to Video" 
                id="video-gen-tab-0" 
                aria-controls="video-gen-tabpanel-0" 
              />
              <Tab 
                icon={<ImageIcon />} 
                label="Image to Video" 
                id="video-gen-tab-1" 
                aria-controls="video-gen-tabpanel-1" 
              />
            </Tabs>
          </Paper>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TabPanel value={tabValue} index={0}>
              {/* Text to Video Content */}
              <TextField
                autoFocus
                multiline
                rows={3}
                fullWidth
                label="Video Description"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the video you want to generate in detail..."
                required
                helperText="Be specific and descriptive for best results. Include details about scene, style, lighting, and action."
              />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {/* Image to Video Content */}
              <TextField
                fullWidth
                label="Starting Image URL"
                value={startImage}
                onChange={(e) => setStartImage(e.target.value)}
                placeholder="Enter the URL of the image you want to animate..."
                required
                error={startImage !== '' && !isValidImageUrl(startImage)}
                helperText={startImage !== '' && !isValidImageUrl(startImage) 
                  ? "Please enter a valid image URL (JPEG, PNG, GIF)" 
                  : "Enter a URL to an image that will be the starting point for your video"
                }
                sx={{ mb: 2 }}
              />
              
              {startImage && isValidImageUrl(startImage) && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <img 
                    src={startImage} 
                    alt="Starting image" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '200px', 
                      objectFit: 'contain',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }} 
                  />
                </Box>
              )}
              
              <TextField
                multiline
                rows={3}
                fullWidth
                label="Animation Description"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe how you want the image to be animated..."
                required
                helperText="Describe the motion, camera movement, or transformation you want to apply to the image."
              />
            </TabPanel>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom display="flex" alignItems="center">
                <TimerIcon sx={{ mr: 1, fontSize: 20 }} />
                Duration
              </Typography>
              <ToggleButtonGroup
                value={duration}
                exclusive
                onChange={handleDurationChange}
                aria-label="video duration"
                fullWidth
              >
                <ToggleButton value={5} aria-label="5 seconds">
                  5 Seconds
                </ToggleButton>
                <ToggleButton value={10} aria-label="10 seconds">
                  10 Seconds
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Longer videos take more time to generate and cost more credits.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom display="flex" alignItems="center">
                <QualityIcon sx={{ mr: 1, fontSize: 20 }} />
                Quality
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={quality}
                  onChange={(e) => setQuality(e.target.value)}
                >
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="hd">HD</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                HD quality takes longer to generate and costs 50% more credits.
              </Typography>
            </Box>
            
            {loading && (
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">{statusMessage}</Typography>
                  <Chip 
                    label={`${Math.round(progress)}%`} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </Box>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Please be patient. Video generation can take several minutes.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !prompt.trim() || (tabValue === 1 && (!startImage || !isValidImageUrl(startImage)))}
            startIcon={loading ? <CircularProgress size={20} /> : <VideoIcon />}
          >
            {loading ? 'Generating...' : 'Generate Video'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default VideoGenerationDialog; 